import { useState, useEffect, useRef, useCallback } from 'react';
import { url } from "../constants/url";

const useWebSocket = (userId, firebaseToken, onMessageCallback = null) => {
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const ws = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const maxReconnectAttempts = 5;
  const reconnectInterval = 3000;

  const connect = useCallback(() => {
    if (!userId || !firebaseToken) {
      //Console.log('WebSocket: Missing userId or token');
      return;
    }

    try {
      // Pass both userId and Firebase token
      const wsUrl = `${url.replace('http', 'ws')}/ws/notifications?userId=${userId}&token=${firebaseToken}`;
      //Console.log('WebSocket: Connecting...');
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        //Console.log('🔌 WebSocket connected');
        setIsConnected(true);
        setReconnectAttempts(0);
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          //Console.log('WebSocket message received:', data);
          
          // Call the callback if provided
          if (onMessageCallback) {
            onMessageCallback(data);
          }
          
          // Trigger custom event for other components
          window.dispatchEvent(new CustomEvent('websocket_message', { detail: data }));
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.current.onclose = (event) => {
        //Console.log('❌ WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        // Attempt reconnect if not normal closure
        if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
          //Console.log(`🔄 Reconnecting... (Attempt ${reconnectAttempts + 1})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts(prev => prev + 1);
            connect();
          }, reconnectInterval);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, [userId, firebaseToken, reconnectAttempts, onMessageCallback]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (ws.current) {
      ws.current.close(1000, 'Manual disconnect');
      ws.current = null;
    }
    
    setIsConnected(false);
    setReconnectAttempts(0);
  }, []);

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  // Ping to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      sendMessage({ type: 'ping', timestamp: new Date().toISOString() });
    }, 30000);

    return () => clearInterval(pingInterval);
  }, [isConnected, sendMessage]);

  // Auto-connect when userId or Firebase token changes
  useEffect(() => {
    if (userId && firebaseToken) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [userId, firebaseToken, connect, disconnect]);

  return {
    isConnected,
    reconnectAttempts,
    sendMessage,
    disconnect,
    connect: () => {
      setReconnectAttempts(0);
      connect();
    }
  };
};

export default useWebSocket;